import { useMemo } from 'react'

import { ActionIcon, Menu, Tooltip, Badge } from '@mantine/core'

import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import DnTable from 'src/components/Core/DnTable'
import IconThreeDots from 'src/icons/IconThreeDots'
import {
  formatDateAbsolute,
  formatDateTimeForTooltip,
} from 'src/lib/formatters'

interface DocumentTemplate {
  documentTemplateId: string
  documentName: string
  documentCategory: string
}

interface DocumentPackage {
  documentTemplatePackageId: string
  packageName: string
  createdAt: string
  documentTemplates: DocumentTemplate[]
}

interface DocumentPackagesTableProps {
  packages: DocumentPackage[]
  loading: boolean
  onRowClick: (packageId: string) => void
  onDeleteClick: (pkg: DocumentPackage) => void
  defaultResultsPerPage?: number
  totalCount?: number
  onPageChange?: (page: number) => void
  paginationProps?: {
    initialPage?: number
    onResultsPerPageChange?: (resultsPerPage: number) => void
  }
}

const DocumentPackagesTable = ({
  packages,
  loading,
  onRowClick,
  onDeleteClick,
  defaultResultsPerPage = 20,
  totalCount = packages.length,
  onPageChange,
  paginationProps,
}: DocumentPackagesTableProps) => {
  const columns = useMemo(
    () => [
      {
        key: 'documentTemplatePackageId',
        label: 'ID',
        width: 5,
        render: (pkg: DocumentPackage) => (
          <CopyableId value={pkg.documentTemplatePackageId} label="Package ID" />
        ),
      },
      {
        key: 'packageName',
        label: 'Package Name',
        width: 25,
        render: (pkg: DocumentPackage) => (
          <Tooltip label={pkg.packageName} withArrow>
            <span className="block max-w-full truncate text-xs text-doubleNickel-gray-900">
              {pkg.packageName}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'documents',
        label: 'Documents',
        width: 45,
        render: (pkg: DocumentPackage) => {
          const docs = pkg.documentTemplates || []
          const maxVisible = 3

          if (!docs.length) {
            return (
              <span className="text-xs text-doubleNickel-gray-400">
                No documents
              </span>
            )
          }

          const visibleDocs = docs.slice(0, maxVisible)
          const hiddenCount = docs.length - maxVisible

          return (
            <div className="flex flex-wrap items-center gap-1">
              {visibleDocs.map((doc) => (
                <Badge
                  key={doc.documentTemplateId}
                  size="xs"
                  variant="light"
                  color="gray"
                >
                  {doc.documentName}
                </Badge>
              ))}
              {hiddenCount > 0 && (
                <Tooltip
                  label={docs
                    .slice(maxVisible)
                    .map((d) => d.documentName)
                    .join(', ')}
                  withArrow
                  multiline
                  maw={300}
                >
                  <Badge size="xs" variant="light" color="blue">
                    +{hiddenCount} more
                  </Badge>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        key: 'createdAt',
        label: 'Created At',
        width: 10,
        render: (pkg: DocumentPackage) => (
          <Tooltip label={formatDateTimeForTooltip(pkg.createdAt)} withArrow>
            <span className="text-xs text-doubleNickel-gray-600">
              {formatDateAbsolute(pkg.createdAt)}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 10,
        render: (pkg: DocumentPackage) => (
          <div className="flex justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-32">
                <Menu.Item
                  className="text-doubleNickel-error-600"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteClick(pkg)
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [onDeleteClick]
  )

  return (
    <DnTable
      columns={columns}
      data={packages}
      loading={loading}
      enableColumnResize={false}
      enablePagination={true}
      defaultResultsPerPage={defaultResultsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      paginationProps={paginationProps}
      onRowClick={(_, row) => {
        const pkg = row as DocumentPackage
        if (pkg?.documentTemplatePackageId) {
          onRowClick(pkg.documentTemplatePackageId)
        }
      }}
      emptyStateMessage="No packages found"
      rowClassName="cursor-pointer hover:bg-doubleNickel-gray-50"
    />
  )
}

export default DocumentPackagesTable
